// WHAT: Settings view component for Supabase database and API configuration
// WHY:  Provides interface for managing Supabase project credentials and database connection
// DOMAIN: Settings → Supabase
// DEPENDENCIES: Supabase config hook, configuration form components, Tauri core API
// RISKS:
//   - WARN: Database URL contains sensitive connection information
//   - NOTE: Two separate configuration methods (API vs direct DB) may cause confusion
//   - WARN: Invalid credentials may not be validated until connection attempt

import { core } from "@tauri-apps/api";
import { Database, Server, Key, Info, Shield, AlertTriangle } from "lucide-react";
import { useState, useEffect } from "react";

import { Card, Stack, Heading, Badge, StatusIndicator } from "@/components/ui";

import { DatabaseUrlForm } from "../components/DatabaseUrlForm";
import { SupabaseConfigForm } from "../components/SupabaseConfigForm";
import { useSupabaseConfig } from "../hooks/useSupabaseConfig";

/**
 * WHAT:
 *   React component for managing Supabase API and database connection configuration.
 *
 * WHY:
 *   Enables users to configure both Supabase API credentials (recommended) and direct
 *   database connection (advanced) for backend infrastructure access.
 *
 * BEHAVIOR:
 *   - Loads Supabase API configuration via custom hook
 *   - Loads database URL status on mount
 *   - Displays separate status indicators for API and database configuration
 *   - Renders configuration forms for both connection methods
 *   - Shows security notices and troubleshooting information
 *
 * RISKS:
 *   - WARN: Database URL contains full connection string with credentials
 *   - NOTE: Two configuration methods may conflict if both are used
 *   - WARN: Configuration validation happens at connection time, not input time
 *
 * @returns {JSX.Element} Supabase settings view component
 *
 */
export function SupabaseSettingsView() {
  const { config: supabaseConfig, loading: loadingSupabase } = useSupabaseConfig();
  const [databaseUrl, setDatabaseUrl] = useState<null | string>(null);
  const [loadingDatabase, setLoadingDatabase] = useState(true);

  // NOTE: Load database URL status on component mount
  // WARN: Database URL may contain sensitive connection credentials
  useEffect(() => {
    const loadDatabaseUrl = async () => {
      try {
        setLoadingDatabase(true);
        const url = await core.invoke<string>("get_database_url_cmd");
        setDatabaseUrl(url || null);
      } catch {
        // NOTE: URL might not be set yet - this is expected for new configurations
        setDatabaseUrl(null);
      } finally {
        setLoadingDatabase(false);
      }
    };

    void loadDatabaseUrl();
  }, []);

  // NOTE: Configuration status checks - both URL and key must be non-empty for API config
  // WARN: These checks only validate presence, not validity of credentials
  const isSupabaseConfigured =
    !loadingSupabase &&
    supabaseConfig !== null &&
    supabaseConfig.url !== "" &&
    supabaseConfig.key !== "";
  const isDatabaseConfigured = !loadingDatabase && databaseUrl !== null && databaseUrl !== "";

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <Database className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              Supabase Configuration
            </Heading>
            <p className="text-slate-300">
              Configure your Supabase database connection and API settings for seamless integration.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Status Indicators */}
      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <Server className="w-5 h-5 text-slate-400" />
          Configuration Status
        </Heading>

        <div className="grid gap-4 md:grid-cols-2">
          <StatusIndicator
            description="Supabase project URL and API key configuration"
            isConfigured={isSupabaseConfigured}
            label="Supabase API"
            showLock
            size="md"
          />
          <StatusIndicator
            description="Direct PostgreSQL database connection string"
            isConfigured={isDatabaseConfigured}
            label="Database URL"
            showLock
            size="md"
          />
        </div>
      </div>

      {/* Information Card */}
      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-blue-400 mb-1">Supabase Integration</h3>
            <p className="text-sm text-blue-300">
              Supabase provides your backend infrastructure including database, authentication,
              real-time subscriptions, and edge functions. Configure your project credentials below.
            </p>
          </div>
        </Stack>
      </Card>

      {/* Configuration Sections */}
      <div className="space-y-6">
        {/* Supabase API Configuration */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Server className="w-5 h-5 text-slate-400" />
            <h3 className="text-lg font-medium text-white">API Configuration</h3>
            <Badge size="sm" style="subtle" variant="default">
              Recommended
            </Badge>
          </div>
          <SupabaseConfigForm />
        </div>

        {/* Database URL Configuration */}
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Key className="w-5 h-5 text-slate-400" />
            <h3 className="text-lg font-medium text-white">Direct Database Access</h3>
            <Badge size="sm" style="subtle" variant="accent">
              Advanced
            </Badge>
          </div>
          <DatabaseUrlForm />
        </div>
      </div>

      {/* Security Notice */}
      <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Shield className="w-5 h-5 text-green-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-green-400 mb-1">Security & Privacy</h3>
            <p className="text-sm text-green-300">
              All credentials are stored locally and encrypted. Your data never leaves your device
              unless you explicitly configure external connections. We recommend using the API
              configuration for most use cases.
            </p>
          </div>
        </Stack>
      </Card>

      {/* Troubleshooting */}
      <Card className="bg-slate-800/20 border-slate-700/30" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <AlertTriangle className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-slate-300 mb-1">Troubleshooting</h3>
            <div className="text-sm text-slate-400 space-y-1">
              <p>• Make sure your Supabase project is active and accessible</p>
              <p>• Verify your API keys have the correct permissions</p>
              <p>• Check your network connection and firewall settings</p>
              <p>• Use the Debug Store feature to diagnose connection issues</p>
            </div>
          </div>
        </Stack>
      </Card>
    </div>
  );
}
